import React, { useState, useEffect, useContext } from 'react';
import { Table } from 'antd';
import APIUtils from '../../api/APIUtils';
import { CommonContext } from '../../Context';
import WebLinks from '../../api/WebLinks';

const SocialReports = () => {
	const commonContext = useContext(CommonContext);
	const buyerId = commonContext?.common?.buyerId;

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	// fetchReports: loads social reports for the current buyer
	const fetchReports = async (page = 1, pageSize = 10) => {
		if (!buyerId) {
			setData([]);
			return;
		}
		setLoading(true);
		setError(null);
		const url = `${WebLinks.GetReports}${buyerId}&page=${page}&per_page=${pageSize}`;
		try {
			const res = await APIUtils.apiCall('GET', url, null, true, true);
			if (res && !res.isCatch && res.resultObj) {
				const responseData = res.resultObj.data;
				console.log(responseData);
				setData(responseData.jobs || []);

				setPagination({
					current: responseData?.data?.current_page || page,
					pageSize: responseData?.data?.per_page || pageSize,
					total: responseData?.data?.total || 0,
				});
			} else {
				setError('Failed to fetch reports');
				setData([]);
			}
		} catch (err) {
			setError('Failed to fetch reports');
			setData([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (buyerId) {
			fetchReports(1, pagination.pageSize);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buyerId]);

	const columns = [
		{
			title: 'Job Title',
			dataIndex: 'title',
			key: 'title',
			ellipsis: true,
			sorter: (a, b) =>
				(a.title || '').localeCompare(b.title || ''),
			sortDirections: ['ascend', 'descend'],
			render: (value) => value || '-',
		},
		{
			title: 'Company',
			dataIndex: 'company_name',
			key: 'company_name',
			ellipsis: true,
			sorter: (a, b) =>
				(a.company_name || '').localeCompare(b.company_name || ''),
			sortDirections: ['ascend', 'descend'],
			render: (value) => value || '-',
		},
		{
			title: 'Location',
			key: 'location',
			ellipsis: true,
			sorter: (a, b) =>
				(a.location || a.city || '').localeCompare(
					b.location || b.city || ''
				),
			sortDirections: ['ascend', 'descend'],
			render: (_, record) => {
				return record.location || record.city || '-';
			},
		},
		{
			title: 'Ad Status',
			key: 'ad_status',
			ellipsis: true,
			sorter: (a, b) =>
				(a.facebook_ad_details?.ad_effective_status || '').localeCompare(
					b.facebook_ad_details?.ad_effective_status || ''
				),
			sortDirections: ['ascend', 'descend'],
			render: (_, record) => {
				return record.facebook_ad_details?.ad_effective_status || '-';
			},
		},
		{
			title: 'Budget',
			dataIndex: 'ad_budget',
			key: 'ad_budget',
			sorter: (a, b) =>
				(Number(a.ad_budget || 0) * 2) -
				(Number(b.ad_budget || 0) * 2),
			sortDirections: ['ascend', 'descend'],
			render: (value) => {
				if (value === null || value === undefined || value === '') {
					return '-';
				}

				return `${(Number(value) * 2).toFixed(2)}`;
			},
		},
		{
			title: 'Spend',
			key: 'spend',
			sorter: (a, b) =>
				(Number(a.facebook_ad_details?.spend || 0) * 2) -
				(Number(b.facebook_ad_details?.spend || 0) * 2),
			sortDirections: ['ascend', 'descend'],
			render: (_, record) => {
				const value = record.facebook_ad_details?.spend;

				if (value === null || value === undefined || value === '') {
					return '-';
				}

				return `${(Number(value) * 2).toFixed(2)}`;
			},
		},
		{
			title: 'Reach',
			key: 'reach',
			sorter: (a, b) =>
				Number(a.facebook_ad_details?.reach || 0) -
				Number(b.facebook_ad_details?.reach || 0),
			sortDirections: ['ascend', 'descend'],
			render: (_, record) => {
				return record.facebook_ad_details?.reach || '-';
			},
		},
		{
			title: 'Clicks',
			key: 'clicks',
			sorter: (a, b) =>
				Number(a.facebook_ad_details?.clicks || 0) -
				Number(b.facebook_ad_details?.clicks || 0),
			sortDirections: ['ascend', 'descend'],
			render: (_, record) => {
				return record.facebook_ad_details?.clicks || '-';
			},
		},
		{
			title: 'CPC',
			key: 'cpc',
			sorter: (a, b) =>
				(Number(a.facebook_ad_details?.cpc || 0) * 2) -
				(Number(b.facebook_ad_details?.cpc || 0) * 2),
			sortDirections: ['ascend', 'descend'],
			render: (_, record) => {
				const value = record.facebook_ad_details?.cpc;

				if (value === null || value === undefined || value === '') {
					return '-';
				}

				return `${(Number(value) * 2).toFixed(2)}`;
			},
		},
	];

	return (
		<div className="py-4">
			{error && <div className="mb-3 text-red-600">{error}</div>}
			<div className="p-4 overflow-auto">
				<Table
					dataSource={data}
					columns={columns}
					loading={loading}
					rowKey={(record, idx) => record.id || record._id || idx}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						pageSizeOptions: [10, 25, 50, 100],
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} of ${total} items`,
					}}
					className="green-table custom-table"
					onChange={(paginationInfo) => {
						fetchReports(
							paginationInfo.current,
							paginationInfo.pageSize
						);
					}}
					scroll={{ x: 'max-content' }}
				/>
			</div>
		</div>
	);
};
export default SocialReports;
